import type { CSSProperties } from "react";
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from "reactflow";
import { X } from "lucide-react";
import { useEditableEdge } from "./EditableEdgeContext";

type Style = {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
};

function styleFor(type: string): Style {
  switch (type) {
    case "MEASUREMENT_FLOW":
      return { stroke: "var(--rd-info-500)", strokeWidth: 2, strokeDasharray: "6 4" };
    case "SENSOR_LINK":
      return { stroke: "var(--border-strong)", strokeWidth: 1.5, strokeDasharray: "2 3" };
    case "DEPENDENCY":
      return { stroke: "var(--text-tertiary)", strokeWidth: 2, strokeDasharray: "8 4" };
    case "QUALITY_CHECK":
      return { stroke: "var(--rd-warning-500)", strokeWidth: 2 };
    case "ALTERNATIVE_FLOW":
      return { stroke: "var(--rd-warning-600)", strokeWidth: 2, strokeDasharray: "10 4" };
    case "PARALLEL_FLOW":
      return { stroke: "var(--rd-success-500)", strokeWidth: 2 };
    case "PRODUCTION_FLOW":
    default:
      return { stroke: "var(--border-strong)", strokeWidth: 2 };
  }
}

function makeEdge(type: string) {
  return function CustomEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    selected,
  }: EdgeProps) {
    const [path, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
    });
    const { isEdit, onDeleteEdge } = useEditableEdge();
    const showDelete = isEdit && selected === true;
    return (
      <>
        <BaseEdge path={path} markerEnd={markerEnd} style={styleFor(type)} />
        {showDelete ? (
          <EdgeLabelRenderer>
            <div
              style={{ "--lx": `${labelX}px`, "--ly": `${labelY}px` } as CSSProperties}
              className="pointer-events-auto absolute [transform:translate(-50%,-50%)_translate(var(--lx),var(--ly))]"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEdge(id);
                }}
                aria-label="Remover conexão"
                title="Remover conexão (Delete/Backspace)"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-rd-danger-500 text-white shadow-sm transition-colors hover:bg-rd-danger-600"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
          </EdgeLabelRenderer>
        ) : null}
      </>
    );
  };
}

export const edgeTypes = {
  PRODUCTION_FLOW: makeEdge("PRODUCTION_FLOW"),
  MEASUREMENT_FLOW: makeEdge("MEASUREMENT_FLOW"),
  SENSOR_LINK: makeEdge("SENSOR_LINK"),
  DEPENDENCY: makeEdge("DEPENDENCY"),
  QUALITY_CHECK: makeEdge("QUALITY_CHECK"),
  ALTERNATIVE_FLOW: makeEdge("ALTERNATIVE_FLOW"),
  PARALLEL_FLOW: makeEdge("PARALLEL_FLOW"),
} as const;
